/* eslint-disable no-shadow */
/* eslint-disable react/require-default-props */
import React, { useState, useEffect } from 'react';
import { DetailedConsumptionReportTable } from '@nile/reports/src/modules/RetinerReport/components/tables';
import { ConsumptionBreakdownChart } from '@nile/reports/src/modules/RetinerReport/components/charts';
import { MinimalDropDown } from '@nile/client_management/src/Containers/ClientDetails/ClientContractNew/ContractForm/Components';
import { AccountClient, GET_RETAINER_CONTRACT_LIST } from '@nile/graphql';
import { SVGIcon } from '@superops/super-icons';
import { useIntl } from 'react-intl';
import { getTranslatedText } from '@nile/components';

function ContractsDropdown({
  sharedReport,
  clientId,
  contract,
  reportId,
  allowedContractIds,
  handleClientClick
}: {
  sharedReport: boolean;
  clientId: string;
  contract?: {
    contractId: string | null;
    contractName?: string;
    contractType?: string | null;
  };
  reportId: string;
  allowedContractIds?: string[];
  handleClientClick: (
    client: { accountId: string; name?: string },
    contract?: {
      contractId: string | null;
      contractName?: string;
      contractType?: string | null;
    }
  ) => void;
}) {
  const intl = useIntl();
  const [contracts, setContracts] = useState<
    {
      contractId: string | null;
      contractName?: string;
      contractType?: string | null;
    }[]
  >([{ contractId: null, contractName: 'All', contractType: null }]);
  const [selectedContract, setSelectedContract] = useState<{
    contractId: string | null;
    contractName?: string;
    contractType?: string | null;
  } | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const getContracts = async () => {
    setLoading(true);
    const response = await AccountClient.query({
      query: GET_RETAINER_CONTRACT_LIST,
      fetchPolicy: 'network-only',
      variables: {
        input: {
          client: { accountId: clientId },
          ...(sharedReport && reportId && { sharedReportId: reportId })
        }
      }
    });
    let contractsList: any[] = response.data.getRetainerContractList;
    if (sharedReport && allowedContractIds?.length) {
      contractsList = contractsList.filter((c: any) =>
        allowedContractIds?.includes(c.contractId)
      );
    }
    const contractsListArray = [];
    if (!allowedContractIds?.length) {
      contractsListArray.push({
        contractId: null,
        contractName: getTranslatedText(intl, 'common.all'),
        contractType: null
      });
    }
    setContracts([...contractsListArray, ...contractsList]);
    if (contract?.contractId && contractsList.length > 0) {
      setSelectedContract(
        contractsList.find((c: any) => c.contractId === contract?.contractId) ??
          null
      );
    } else {
      setSelectedContract(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    getContracts();
  }, [clientId]);

  return (
    <div className="w300" key={clientId}>
      <MinimalDropDown
        options={contracts?.map((c) => ({
          label: c.contractName,
          value: c.contractId
        }))}
        onChange={(option: { label: string; value: string }) => {
          if (option?.value) {
            const chosenContract = contracts.find(
              (c: any) => c.contractId === option.value
            );
            setSelectedContract(chosenContract ?? null);
            handleClientClick({ accountId: clientId }, chosenContract);
          } else {
            setSelectedContract(null);
            handleClientClick({ accountId: clientId });
          }
        }}
        value={
          selectedContract?.contractId && selectedContract?.contractName
            ? {
                label: selectedContract?.contractName,
                value: selectedContract?.contractId
              }
            : {
                label: getTranslatedText(intl, 'common.all'),
                value: null
              }
        }
        placeholder="Select Contract"
        isSearchable
        isLoading={loading}
        isDisabled={loading}
      />
    </div>
  );
}

const DetailedConsumptionReport = ({
  sharedReport,
  clientId,
  contract,
  reportId,
  allowedContractIds,
  allowedDateRange,
  handleClientClick,
  resetReportClientFilter
}: {
  sharedReport: boolean;
  clientId: string;
  contract?: {
    contractId: string | null;
    contractName?: string;
    contractType?: string | null;
  };
  reportId: string;
  allowedContractIds?: string[];
  allowedDateRange?: { start: string; end: string };
  handleClientClick: (
    client: { accountId: string; name?: string },
    contract?: {
      contractId: string | null;
      contractName?: string;
      contractType?: string | null;
    }
  ) => void;
  resetReportClientFilter: () => void;
}) => {
  return (
    <div className="full-width full-height scroll">
      <div className="m10 rounded-4px white-to-grey-bg bshadow fx fw actr p2040 g20">
        {!sharedReport && (
          <span
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                resetReportClientFilter();
              }
            }}
            className="ptr"
            onClick={resetReportClientFilter}
          >
            <SVGIcon name="BackArrow" />
          </span>
        )}
        <ContractsDropdown
          sharedReport={sharedReport}
          clientId={clientId}
          contract={contract}
          allowedContractIds={allowedContractIds}
          reportId={reportId}
          handleClientClick={handleClientClick}
        />
      </div>
      <div className="m20">
        <ConsumptionBreakdownChart
          clientId={clientId}
          reportId={reportId}
          sharedReport={sharedReport}
          contractId={contract?.contractId ?? undefined}
        />
      </div>
      <div className="m20 rounded-4px white-to-grey-bg bshadow">
        <DetailedConsumptionReportTable
          reportId={reportId}
          clientId={clientId}
          contract={contract}
          sharedReport={sharedReport}
          allowedDateRange={allowedDateRange}
        />
      </div>
    </div>
  );
};

export default DetailedConsumptionReport;
